import type { ForecastContextSelectedOptions } from "@appretio/context-selector";

/** Values a context field currently offers, as the option queries return them. */
type AvailableContextOptions = Record<string, string[] | null | undefined>;

/**
 * The values each field offers: the base catalogue and the filtered one,
 * unioned. That is what ContextDropdown renders (the filtered values, then
 * the base ones they don't cover), so pruning against anything narrower
 * could drop a value the user can still see and pick.
 */
export function availableContextOptions(
  base: AvailableContextOptions | undefined,
  filtered: AvailableContextOptions | undefined
): AvailableContextOptions {
  const available: AvailableContextOptions = {};
  for (const source of [base, filtered]) {
    for (const [field, values] of Object.entries(source ?? {})) {
      if (!values?.length) continue;
      const known = available[field];
      available[field] = known?.length
        ? [...new Set([...known, ...values])]
        : values;
    }
  }
  return available;
}

/**
 * Whether a field offers a value. A field with no list offers anything:
 * the prune leaves it alone.
 */
export function contextOffers(
  available: AvailableContextOptions,
  field: string,
  value: string
): boolean {
  const values = available[field];
  return !values?.length || values.includes(value);
}

/**
 * Drops selected values a field no longer offers — the state equivalent
 * of the user deselecting them by hand.
 *
 * ``selected`` is returned unchanged when there is nothing to prune, so
 * feeding the result into a ``setState`` bails out instead of
 * re-rendering.
 *
 * Fields whose available list is missing or empty are left alone: option
 * lists arrive asynchronously and refetch on every version / filter
 * change, so "no values yet" must never be read as "nothing on offer".
 */
export function pruneStaleContextSelections(
  selected: ForecastContextSelectedOptions,
  availableByField: AvailableContextOptions
): ForecastContextSelectedOptions {
  if (!selected) return selected;

  let pruned: Record<string, string[]> | undefined;

  for (const [field, values] of Object.entries(selected)) {
    if (!values?.length) continue;
    const kept = values.filter((value) =>
      contextOffers(availableByField, field, value)
    );
    if (kept.length === values.length) continue;

    pruned ??= { ...selected };
    // Matches ContextSelector, which removes the key rather than keeping
    // an empty array when the user clears a field's last value.
    if (kept.length === 0) delete pruned[field];
    else pruned[field] = kept;
  }

  return pruned ?? selected;
}
